/**
 * NetworkGraphControl
 *
 * Network graph for relationship visualization
 * Node-link diagram with force-directed layout
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData } from './BaseChartComponent';

interface Node {
  id: string;
  label: string;
  x: number;
  y: number;
  size: number;
}

interface Edge {
  source: string;
  target: string;
}

const NetworkGraphControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);

  const networkConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      sourceKey: (config?.sourceKey as string) || 'source',
      targetKey: (config?.targetKey as string) || 'target',
      labelKey: (config?.labelKey as string) || 'label',
      nodeRadius: (config?.nodeRadius as number) || 15,
    };
  }, [control.config]);

  const renderNetwork = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Extract nodes and edges
    const edges: Edge[] = [];
    const nodeMap = new Map<string, Node>();

    chartData.forEach((d) => {
      const source = String((d as Record<string, unknown>)[networkConfig.sourceKey] || '');
      const target = String((d as Record<string, unknown>)[networkConfig.targetKey] || '');

      if (source && target) {
        edges.push({ source, target });

        // Ensure nodes exist
        if (!nodeMap.has(source)) {
          nodeMap.set(source, {
            id: source,
            label: String((d as Record<string, unknown>)[networkConfig.labelKey] || source),
            x: Math.random() * 400 + 100,
            y: Math.random() * 300 + 50,
            size: networkConfig.nodeRadius,
          });
        }
        if (!nodeMap.has(target)) {
          nodeMap.set(target, {
            id: target,
            label: target,
            x: Math.random() * 400 + 100,
            y: Math.random() * 300 + 50,
            size: networkConfig.nodeRadius,
          });
        }
      }
    });

    if (nodeMap.size === 0) return null;

    const nodes = Array.from(nodeMap.values());
    const width = 600;
    const height = (control.config?.height as number) || 400;

    // Simple spring layout approximation (single iteration)
    const k = 50; // Spring constant
    const c = 0.1; // Damping

    nodes.forEach((node) => {
      let fx = 0;
      let fy = 0;

      // Repulsion from other nodes
      nodes.forEach((other) => {
        if (node.id === other.id) return;

        const dx = node.x - other.x;
        const dy = node.y - other.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        fx += (dx / dist) * (k / dist);
        fy += (dy / dist) * (k / dist);
      });

      // Attraction to connected nodes
      edges.forEach((edge) => {
        const other =
          edge.source === node.id
            ? nodeMap.get(edge.target)
            : edge.target === node.id
              ? nodeMap.get(edge.source)
              : null;

        if (!other) return;

        const dx = other.x - node.x;
        const dy = other.y - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        fx += (dx / dist) * (dist / k) * 0.5;
        fy += (dy / dist) * (dist / k) * 0.5;
      });

      // Boundary constraints
      node.x = Math.max(20, Math.min(width - 20, node.x + fx * c));
      node.y = Math.max(20, Math.min(height - 20, node.y + fy * c));
    });

    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c',
      '#d084d0', '#13c2c2', '#f5ad14', '#eb2f96', '#52c41a',
    ];

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Edges */}
        {edges.map((edge, idx) => {
          const source = nodeMap.get(edge.source);
          const target = nodeMap.get(edge.target);

          if (!source || !target) return null;

          return (
            <line
              key={`edge-${idx}`}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              stroke="#d1d5db"
              strokeWidth="1"
              opacity="0.6"
            />
          );
        })}

        {/* Nodes */}
        {nodes.map((node, idx) => {
          return (
            <g key={`node-${node.id}`}>
              <circle
                cx={node.x}
                cy={node.y}
                r={node.size}
                fill={colors[idx % colors.length]}
                opacity="0.8"
                stroke="#fff"
                strokeWidth="2"
              />
              <text
                x={node.x}
                y={node.y + 5}
                textAnchor="middle"
                fontSize="11"
                fill="white"
                fontWeight="bold"
              >
                {node.label.substring(0, 4)}
              </text>
            </g>
          );
        })}
      </svg>
    );
  }, [chartData, networkConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="network-graph">
      <div style={{ width: '100%', height: '100%' }}>{renderNetwork}</div>
    </BaseChartComponent>
  );
});

NetworkGraphControl.displayName = 'NetworkGraphControl';

export default NetworkGraphControl;
